import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Paper,
  TextField,
  Typography,
  useTheme,
  Snackbar,
  Alert,
  Divider,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  ListItemText
} from '@mui/material';
import Grid from '@mui/material/Grid';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventIcon from '@mui/icons-material/Event';
import HomeIcon from '@mui/icons-material/Home';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import UploadIcon from '@mui/icons-material/Upload';
import ScheduleImportModal from '../Schedules/ScheduleImportModal';

const getCSRFToken = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') {
      return value;
    }
  }
  return null;
};

const SectionPeriodCreate = ({ blockSchedule, gradeLevel, onSectionPeriodCreated }) => {
  const theme = useTheme();
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [availableDays] = useState(['Monday', 'Tuesday', 'Wednesday', 'Thursday']);
  const [homeroom, setHomeroom] = useState(false);
  const [periodType, setPeriodType] = useState('regular');
  const [selectedGradeLevels, setSelectedGradeLevels] = useState([]);
  const [availableGradeLevels, setAvailableGradeLevels] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Check if both block schedule and grade level are selected for import
  const canImport = blockSchedule && (gradeLevel || selectedGradeLevels.length > 0);
  const importTargetGrade = selectedGradeLevels.length === 1 ? selectedGradeLevels[0] : gradeLevel;

  // Fetch available grade levels
  useEffect(() => {
    axios.get('/courses/api/grade_levels/')
      .then(response => {
        setAvailableGradeLevels(response.data);
        // Set default selection to current grade level if provided
        if (gradeLevel) {
          setSelectedGradeLevels([gradeLevel]);
        }
      })
      .catch(error => console.error('Error fetching grade levels:', error));
  }, [gradeLevel]);

  // Reset period type if CTE Flexible Block is selected but grades are not 11-12
  useEffect(() => {
    if (periodType === 'cte_flexible') {
      const hasValidGrades = selectedGradeLevels.some(gradeId => {
        const grade = availableGradeLevels.find(g => g.id === gradeId);
        return grade && (grade.grade === '11' || grade.grade === '12');
      });
      
      if (!hasValidGrades) {
        setPeriodType('regular');
      }
    }
  }, [selectedGradeLevels, periodType, availableGradeLevels]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!startTime || !endTime) {
      setError('Please provide both start and end times');
      return;
    }
    
    if (daysOfWeek.length === 0) {
      setError('Please select at least one day');
      return;
    }

    // For CTE Flexible Block, restrict to grades 11-12 only
    let cteGradeLevels = [];
    if (periodType === 'cte_flexible') {
      cteGradeLevels = availableGradeLevels
        .filter(grade => grade.grade === '11' || grade.grade === '12')
        .map(grade => grade.id);
    }

    const sectionPeriodData = {
      start_time: startTime,
      end_time: endTime,
      block_schedule: blockSchedule,
      grade_level: periodType === 'cte_flexible' ? null : (selectedGradeLevels.length === 1 ? selectedGradeLevels[0] : gradeLevel),
      grade_levels: periodType === 'cte_flexible' ? cteGradeLevels : (selectedGradeLevels.length > 1 ? selectedGradeLevels : []),
      days_of_week: daysOfWeek,
      homeroom: homeroom,
      period_type: periodType,
    };

    axios.post('/schedules/api/section-periods/', sectionPeriodData, {
      headers: { 'X-CSRFToken': getCSRFToken() },
    })
    .then(response => {
      setSuccess(true);
      onSectionPeriodCreated();
      setStartTime('');
      setEndTime('');
      setDaysOfWeek([]);
      setHomeroom(false);
      setPeriodType('regular');
      setSelectedGradeLevels(gradeLevel ? [gradeLevel] : []);
      setError('');
      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    })
    .catch(error => {
      console.error('Error creating section period:', error);
      setError(error.response?.data?.message || 'Failed to create section period');
    });
  };

  const handleDaySelection = (day) => {
    setDaysOfWeek(prev => 
      prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const handleImportSuccess = (importResults) => {
    setSuccess(true);
    onSectionPeriodCreated();
    // Show success message with import results
    setTimeout(() => setSuccess(false), 5000);
  };

  const handleCloseSnackbar = () => {
    setError('');
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 3, 
        mb: 4,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        maxWidth: 900,
        mx: 'auto'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <ScheduleIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6" component="h2">
          Create Section Period
        </Typography>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth margin="normal" required>
              <TextField
                label="Start Time"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth margin="normal" required>
              <TextField
                label="End Time"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
            </FormControl>
          </Grid>
          
          <Grid size={12}>
            <FormControl fullWidth margin="normal" required>
              <InputLabel id="period-type-label">Period Type</InputLabel>
              <Select
                labelId="period-type-label"
                value={periodType}
                label="Period Type"
                onChange={(e) => setPeriodType(e.target.value)}
              >
                <MenuItem value="regular">Regular Class Period</MenuItem>
                <MenuItem value="homeroom">Homeroom</MenuItem>
                {/* Only show CTE Flexible Block for grades 11-12 */}
                {selectedGradeLevels.some(gradeId => {
                  const grade = availableGradeLevels.find(g => g.id === gradeId);
                  return grade && (grade.grade === '11' || grade.grade === '12');
                }) && (
                  <MenuItem value="cte_flexible">CTE Flexible Block (Grades 11-12 Only)</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          {periodType !== 'cte_flexible' && (
            <Grid size={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="grade-levels-label">Grade Levels</InputLabel>
                <Select
                  labelId="grade-levels-label"
                  multiple
                  value={selectedGradeLevels}
                  label="Grade Levels"
                  onChange={(e) => setSelectedGradeLevels(e.target.value)}
                  renderValue={(selected) => 
                    selected.map(id => {
                      const grade = availableGradeLevels.find(g => g.id === id);
                      return grade ? grade.grade : id;
                    }).join(', ')
                  }
                >
                  {availableGradeLevels.map((grade) => (
                    <MenuItem key={grade.id} value={grade.id}>
                      <Checkbox checked={selectedGradeLevels.indexOf(grade.id) > -1} />
                      <ListItemText primary={grade.grade} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {periodType === 'cte_flexible' && (
            <Grid size={12}>
              <Box sx={{ 
                p: 2, 
                backgroundColor: theme.palette.info.light, 
                borderRadius: 1,
                border: `1px solid ${theme.palette.info.main}`
              }}>
                <Typography variant="body2" color="info.dark">
                  <strong>CTE Flexible Block:</strong> This period will automatically be available to grades 11-12 only. 
                  Grades 9-10 have their own dedicated CTE periods and should not use flexible blocks.
                </Typography>
              </Box>
            </Grid>
          )}
          
          <Grid size={12}>
            <FormControl component="fieldset" fullWidth margin="normal">
              <FormLabel component="legend" sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EventIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
                  Select Days of the Week
                </Box>
              </FormLabel>
              <FormGroup row>
                {availableDays.map((day) => (
                  <FormControlLabel
                    key={day}
                    control={
                      <Checkbox
                        checked={daysOfWeek.includes(day)}
                        onChange={() => handleDaySelection(day)}
                        color="primary"
                      />
                    }
                    label={day}
                    sx={{ mr: 2 }}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Grid>
          
          <Grid size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={homeroom}
                  onChange={(e) => setHomeroom(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <HomeIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
                  Homeroom Period
                </Box>
              }
            />
          </Grid>
          
          <Grid size={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<AddCircleOutlineIcon />}
              fullWidth
              size="large"
              sx={{ mt: 1 }}
            >
              Create Section Period
            </Button>
          </Grid>
          
          <Grid size={12}>
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>
          </Grid>
          
          <Grid size={12}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<UploadIcon />}
              fullWidth
              size="large"
              onClick={() => setShowImportModal(true)}
              disabled={!canImport}
              sx={{ 
                py: 1.5,
                border: canImport ? '2px dashed' : '2px solid',
                borderColor: canImport ? 'secondary.main' : 'grey.300',
                '&:hover': canImport ? {
                  borderColor: 'secondary.dark',
                  backgroundColor: 'secondary.light',
                  opacity: 0.1
                } : {},
                '&.Mui-disabled': {
                  borderColor: 'grey.300',
                  backgroundColor: 'grey.50',
                  color: 'grey.500'
                }
              }}
            >
              📅 Import Periods from Spreadsheet
            </Button>
            <Typography 
              variant="caption" 
              color={canImport ? "text.secondary" : "error"} 
              sx={{ display: 'block', textAlign: 'center', mt: 1 }}
            >
              {canImport 
                ? "Upload Excel/CSV file with period names and times" 
                : "⚠️ Please select both Term (Block Schedule) and Grade Level first"
              }
            </Typography>
            {!canImport && (
              <Box sx={{ mt: 1, p: 1, backgroundColor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                <Typography variant="caption" color="warning.dark" sx={{ display: 'block', textAlign: 'center' }}>
                  <strong>Required for Import:</strong>
                  <br />• Select a Term (Block Schedule) from the dropdown above
                  <br />• Select a Grade Level for the periods
                </Typography>
              </Box>
            )}
          </Grid>
          
          {daysOfWeek.length > 0 && (
            <Grid size={12}>
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Selected Days:
                </Typography>
                {daysOfWeek.map(day => (
                  <Chip 
                    key={day} 
                    label={day} 
                    size="small" 
                    onDelete={() => handleDaySelection(day)}
                  />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </form>
      
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={success} 
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Section period created successfully!
        </Alert>
      </Snackbar>
      
      <ScheduleImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        gradeLevel={importTargetGrade}
        blockSchedule={blockSchedule}
        onSuccess={handleImportSuccess}
      />
    </Paper>
  );
};

export default SectionPeriodCreate;
